import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { schoolApi } from '../../api/endpoints.js';
import { Button, Card, Chips, Empty, Muted, Spinner, Tag, Text, useAsync } from '../../ui.jsx';

const categories = [
  { value: 'All', label: 'All types' },
  { value: 'coe', label: 'Public training colleges' },
  { value: 'pcoe', label: 'Private training colleges' }
];

const categoryLabel = {
  coe: 'Public college of education',
  pcoe: 'Private college of education'
};

const PAGE = 30;

export default function Schools() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [listing, setListing] = useState('All');
  const [limit, setLimit] = useState(PAGE);

  const { data, loading, error } = useAsync(
    () => schoolApi.list({ q: term, category, listing }),
    [term, category, listing]
  );

  // Search while the student types. Waiting for the button made the list feel
  // stale, especially on a phone where the keyboard hides most of the results.
  useEffect(() => {
    const timer = setTimeout(() => {
      setTerm(q.trim());
      setLimit(PAGE);
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  const schools = data?.schools || [];
  const directory = data?.directory || [];
  const shown = listing === 'ready' ? schools : directory;

  const submit = (event) => {
    event.preventDefault();
    setLimit(PAGE);
    setTerm(q.trim());
  };

  return (
    <>
      <h2>Training colleges</h2>
      <Muted>
        Every public and private College of Education in Ghana is listed and open for applications.
      </Muted>

      <Card>
        <form onSubmit={submit} className="row search-form">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              label="Search"
              hint="Results update as you type."
              placeholder="Name, short name or town"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <Button type="submit">Search</Button>
        </form>
        <div style={{ marginTop: 12 }}>
          <Chips
            label="College type"
            options={categories}
            value={category}
            onChange={(v) => {
              setCategory(v);
              setLimit(PAGE);
            }}
          />
        </div>
        <div style={{ marginTop: 10 }} className="row">
          <Button size="sm" variant={listing === 'ready' ? undefined : 's'} onClick={() => { setListing('ready'); setLimit(PAGE); }}>
            Open for applications
          </Button>
          <Button size="sm" variant={listing === 'all' ? undefined : 's'} onClick={() => { setListing('all'); setLimit(PAGE); }}>
            Full directory
          </Button>
        </div>
      </Card>

      {loading ? <Spinner label="Loading schools" /> : null}
      {error ? <div className="note">We could not load the school list. Please try again.</div> : null}

      {data && !loading ? (
        <Card>
          <div className="row sp" style={{ marginBottom: 6 }}>
            <b>
              {listing === 'ready'
                ? `${schools.length} open for applications`
                : `${directory.length} listed training colleges`}
            </b>
            {data.directoryTotal && listing === 'all' ? (
              <span className="mu">{data.directoryTotal} in the directory</span>
            ) : null}
          </div>

          {shown.length === 0 ? (
            <Muted>No training college matches that search.</Muted>
          ) : (
            shown.slice(0, limit).map((item) => (
              <div className="prog" key={item.id}>
                <i className="ub">{item.shortName}</i>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{item.name}</b>
                  <div className="mu">
                    {item.city}
                    {item.category ? ` · ${categoryLabel[item.category] || item.category}` : ''}
                    {item.note ? ` · ${item.note}` : ''}
                  </div>
                  {listing === 'ready' ? (
                    <div className="mu">
                      {item.programmeCount} {item.programmeCount === 1 ? 'programme' : 'programmes'}
                      {item.statusLabel ? ` · ${item.statusLabel}` : ''}
                    </div>
                  ) : null}
                </div>
                <div style={{ textAlign: 'right' }}>
                  {listing === 'ready' ? (
                    <>
                      <Tag tone={item.isClosed ? 'no' : item.deadline ? 'ok' : 'mid'}>
                        {item.statusLabel}
                      </Tag>
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" onClick={() => navigate(`/schools/${item.slug}`)}>Open</Button>
                      </div>
                    </>
                  ) : item.canApply ? (
                    <>
                      <Tag tone="ok">Open for applications</Tag>
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" onClick={() => navigate(`/schools/${item.schoolSlug}`)}>Open</Button>
                      </div>
                    </>
                  ) : (
                    <Tag>Listed, not yet on Entrance</Tag>
                  )}
                </div>
              </div>
            ))
          )}

          {shown.length > limit ? (
            <div style={{ marginTop: 14 }}>
              <Button variant="s" onClick={() => setLimit((l) => l + PAGE)}>
                Show more ({shown.length - limit} remaining)
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      {!loading && !data && !error ? <Empty title="No training colleges loaded" /> : null}
    </>
  );
}
